import React from 'react';
import { Alert, Tag, Tooltip, Typography } from 'antd';

import { AssessmentValidation, JsonObject } from './types';

/**
 * Single-line table cell for long identifiers (codes, model versions, class
 * or trainer names). The global stylesheet forces `white-space: nowrap` on
 * every antd table cell, so in fixed-layout tables long values paint over the
 * neighbouring cells; this clips them with an ellipsis and exposes the full
 * value in a tooltip instead.
 */
export const EllipsisText: React.FC<{
  value?: string | number | null;
  code?: boolean;
}> = ({ value, code }) => {
  if (value == null || value === '') return <>—</>;
  const text = String(value);
  return (
    <Tooltip title={text} placement="topLeft">
      <span
        className={
          code ? 'assessment-ellipsis assessment-code' : 'assessment-ellipsis'
        }
      >
        {text}
      </span>
    </Tooltip>
  );
};

export const parseJsonObject = (value: unknown, label: string): JsonObject => {
  if (value == null || value === '') return {};
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as JsonObject;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(String(value));
  } catch (_error) {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as JsonObject;
};

export const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '—';

export const formatScore = (value: number | null | undefined) =>
  value == null ? '—' : Number(value).toFixed(2);

/**
 * The life of an answer key, in one tag.
 *
 * RETIRED is not a shade of draft (D8). Nothing is ever deleted here: when a
 * family is regenerated the key it replaced is retired, and the sessions it
 * already graded keep pointing at it forever. Both of those states used to
 * paint the same grey, so a key that is out of service for good read as one
 * somebody is still writing — the opposite end of the same life.
 */
export const ScenarioStatusTag: React.FC<{
  label: string;
  editable?: boolean;
}> = ({ label, editable }) => {
  const color =
    label === 'active'
      ? 'green'
      : label === 'retired'
        ? 'purple'
        : editable
          ? 'blue'
          : 'default';
  return <Tag color={color}>{label.toUpperCase()}</Tag>;
};

export const QueueStatusTag: React.FC<{ label: string }> = ({ label }) => {
  const color =
    label === 'done'
      ? 'green'
      : label === 'failed'
        ? 'red'
        : label === 'running'
          ? 'processing'
          : 'default';
  return <Tag color={color}>{label.toUpperCase()}</Tag>;
};

export const JsonBlock: React.FC<{ value: unknown; compact?: boolean }> = ({
  value,
  compact,
}) => (
  <pre className={compact ? 'assessment-json compact' : 'assessment-json'}>
    {JSON.stringify(value ?? {}, null, 2)}
  </pre>
);

const issueText = (issue: unknown) => {
  if (typeof issue === 'string') return issue;
  if (issue && typeof issue === 'object') {
    const item = issue as Record<string, unknown>;
    return String(item.message || item.detail || JSON.stringify(item));
  }
  return String(issue);
};

export const ValidationPanel: React.FC<{
  validation: AssessmentValidation | null;
  loading?: boolean;
}> = ({ validation, loading }) => {
  if (loading)
    return <Typography.Text type="secondary">Validating…</Typography.Text>;
  if (!validation) {
    return (
      <Typography.Text type="secondary">
        Validation has not been loaded.
      </Typography.Text>
    );
  }
  return (
    <div className="assessment-validation-list">
      <Alert
        showIcon
        type={validation.runtime_preflight_passed ? 'success' : 'error'}
        title={
          validation.runtime_preflight_passed
            ? 'Existing runtime preflight passes'
            : 'Existing runtime preflight has blockers'
        }
        description={
          validation.runtime_blockers.length
            ? validation.runtime_blockers.map((item, index) => (
                <div key={index}>{issueText(item)}</div>
              ))
            : 'No runtime blockers were returned by the current assessment preflight.'
        }
      />
      {validation.advisories.map((item, index) => (
        <Alert
          key={`${item.code || 'advisory'}-${index}`}
          showIcon
          type="warning"
          title={item.message || item.code || 'Advisory'}
          description={
            item.context ? (
              <JsonBlock compact value={item.context} />
            ) : undefined
          }
        />
      ))}
      {!validation.advisories.length && (
        <Typography.Text type="secondary">
          No additional advisories.
        </Typography.Text>
      )}
    </div>
  );
};
